import Papa from "papaparse";

export const BATCH_CHUNK_SIZE = 100;

type ImportBatch = { start: number; end: number; monitor: boolean };

export function buildImportBatches({
  contactsCount,
  monitorLimit,
}: {
  contactsCount: number;
  monitorLimit: number;
}): ImportBatch[] {
  const batches: ImportBatch[] = [];
  let start = 0;
  while (start < contactsCount) {
    const monitor = start < monitorLimit;
    const boundary = monitor
      ? Math.min(monitorLimit, contactsCount)
      : contactsCount;
    const end = Math.min(start + BATCH_CHUNK_SIZE, boundary);
    batches.push({ start, end, monitor });
    start = end;
  }
  return batches;
}

const CSV_TEMPLATE_HEADERS = [
  "First Name",
  "Last Name",
  "Full Name",
  "Preferred Name",
  "Email",
  "Phone",
  "Company",
  "Job Title",
  "Timezone",
  "Address",
  "City",
  "State",
  "Postal Code",
  "Instagram",
  "LinkedIn",
  "Facebook",
  "Links",
] as const;

const REQUIRED_HEADERS: (typeof CSV_TEMPLATE_HEADERS)[number][] = [
  "First Name",
];

const NAME_HEADER_ALTERNATIVES: (typeof CSV_TEMPLATE_HEADERS)[number][] = [
  "Last Name",
  "Full Name",
];

export type ParsedCsvRow = Record<
  (typeof CSV_TEMPLATE_HEADERS)[number],
  string
>;

type ParseContactsCsvResult =
  | { ok: true; rows: ParsedCsvRow[] }
  | { ok: false; error: string };

export function parseContactsCsv(
  fileContents: string,
  maxContacts: number,
): ParseContactsCsvResult {
  const result = Papa.parse<ParsedCsvRow>(fileContents, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim(),
  });

  if (result.errors.length > 0) {
    return {
      ok: false,
      error: "Couldn't read this file. Make sure it's a valid CSV.",
    };
  }

  const headers = result.meta.fields ?? [];
  const missingHeaders = REQUIRED_HEADERS.filter(
    (header) => !headers.includes(header),
  );
  const hasNameAlternative = NAME_HEADER_ALTERNATIVES.some((header) =>
    headers.includes(header),
  );
  if (missingHeaders.length > 0 || !hasNameAlternative) {
    const missing = hasNameAlternative
      ? missingHeaders
      : [...missingHeaders, NAME_HEADER_ALTERNATIVES.join(" or ")];
    return {
      ok: false,
      error: `This file is missing required columns: ${missing.join(", ")}. Use the template below to make sure your headers match.`,
    };
  }

  if (result.data.length === 0) {
    return { ok: false, error: "This file doesn't have any contacts in it." };
  }

  if (result.data.length > maxContacts) {
    return {
      ok: false,
      error: `This file has ${result.data.length} contacts, but only ${maxContacts} can be imported at a time. Split it into smaller files and try again.`,
    };
  }

  return { ok: true, rows: result.data };
}

export function downloadCsvTemplate() {
  const csv = Papa.unparse({ fields: [...CSV_TEMPLATE_HEADERS], data: [] });
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "contacts-template.csv";
  link.click();
  URL.revokeObjectURL(url);
}
